import { useCallback, useRef } from 'react';
import useUiuxStore from 'src/store/useUiuxStore';

//공통코드 경고문 요청 객체
export type CommonCodeRequirement = {
	key: string;
	list: unknown[];
	message: string;
};

//공통코드 검증 결과 객체(validateCommonCodeLists 결과)
export type CommonCodeValidateResult = {
	valid: boolean;
	missing: CommonCodeRequirement[];
};

export const useCommonCodeValidate = () => {
	const { setConfirmModalVisible, setConfirmModalProps } = useUiuxStore();
	const shownWarningsRef = useRef(new Set<string>());

	const clearShownWarnings = useCallback(() => {
		shownWarningsRef.current.clear();
	}, []);

	//실질적으로 모달을 보여주는 함수(validateCommonCodeLists에서 사용)
	const showCommonCodeWarningModal = useCallback((missing: CommonCodeRequirement[]) => {
		if (missing.length === 0) return;

		const closeModal = () => {
			setConfirmModalVisible(false);
			setConfirmModalProps(undefined);
		};

		setConfirmModalProps({
			title: '설정 오류',
			message: missing.map((item) => item.message).join('\n'),
			confirmText: '확인',
			onConfirm: () => { closeModal(); },
		});
		setConfirmModalVisible(true);
	}, [setConfirmModalProps, setConfirmModalVisible]);

	//list.length === 0인 항목을 찾아 { valid, missing } 반환
	const validateCommonCodeLists = useCallback((
		requirements: CommonCodeRequirement[],
	): CommonCodeValidateResult => {
		const missing = requirements.filter((requirement) => requirement.list.length === 0);

		if (missing.length === 0) {
			return { valid: true, missing: [] };
		}

		const warningKey = missing.map((item) => item.key).join('|');
		if (!shownWarningsRef.current.has(warningKey)) {
			shownWarningsRef.current.add(warningKey);
			showCommonCodeWarningModal(missing);
		}

		return { valid: false, missing };
	}, [showCommonCodeWarningModal]);

	return {
		validateCommonCodeLists,
		showCommonCodeWarningModal,
		clearShownWarnings,
	};
};
